import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Toaster } from "@/components/ui/sonner";
import { Plus, Pencil, Trash2, ImagePlus, X, Loader2, Building2, Download } from "lucide-react";
import { AdminHeader } from "../../components/AdminHeader";
import { adminApi, clearToken, getToken } from "../../lib/adminApi";

const EMPTY = {
  name: "", developer: "", emirate: "Dubai", location: "", bedrooms: "", size: "", handover: "",
  price: "", currency: "AED", tag: "New Launch", description: "",
  image: "", gallery: [], highlights: [""],
  payment: [{ label: "On booking", value: "" }, { label: "During construction", value: "" }, { label: "On handover", value: "" }],
  published: true,
};
const field = "w-full rounded-xl border border-navy/12 bg-white px-4 py-2.5 text-sm text-navy outline-none transition focus:border-gold focus:ring-2 focus:ring-gold/25";

const readFile = (file) => new Promise((res) => { const r = new FileReader(); r.onload = () => res(r.result); r.readAsDataURL(file); });

export default function AdminProjects() {
  const nav = useNavigate();
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [importing, setImporting] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await adminApi.listProjects();
      setProjects(data);
    } catch (err) {
      if (err.response?.status === 401) { clearToken(); nav("/admin"); }
      else toast.error("Could not load projects");
    } finally { setLoading(false); }
  }, [nav]);

  useEffect(() => { if (!getToken()) { nav("/admin"); return; } load(); }, [load, nav]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value }));

  const onMainImage = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    if (file.size > 2 * 1024 * 1024) { toast.error("Image too large (max 2MB)"); return; }
    const data = await readFile(file);
    setForm((f) => ({ ...f, image: data }));
  };
  const onGallery = async (e) => {
    const files = Array.from(e.target.files || []).slice(0, 4);
    const imgs = [];
    for (const file of files) { if (file.size <= 2 * 1024 * 1024) imgs.push(await readFile(file)); }
    setForm((f) => ({ ...f, gallery: [...f.gallery, ...imgs].slice(0, 6) }));
  };
  const removeGallery = (i) => setForm((f) => ({ ...f, gallery: f.gallery.filter((_, idx) => idx !== i) }));

  const setHighlight = (i, v) => setForm((f) => { const h = [...f.highlights]; h[i] = v; return { ...f, highlights: h }; });
  const addHighlight = () => setForm((f) => ({ ...f, highlights: [...f.highlights, ""] }));
  const removeHighlight = (i) => setForm((f) => ({ ...f, highlights: f.highlights.filter((_, idx) => idx !== i) }));

  const setPay = (i, k, v) => setForm((f) => { const p = [...f.payment]; p[i] = { ...p[i], [k]: v }; return { ...f, payment: p }; });

  const save = async (e) => {
    e.preventDefault();
    if (!form.name) { toast.error("Please add a project name"); return; }
    setSaving(true);
    const payload = { ...form, highlights: form.highlights.filter((h) => h.trim()) };
    try {
      if (form.id) await adminApi.updateProject(form.id, payload);
      else await adminApi.createProject(payload);
      toast.success(form.id ? "Project updated" : "Project published");
      setForm(null); load();
    } catch (err) {
      if (err.response?.status === 401) { clearToken(); nav("/admin"); }
      else toast.error("Save failed");
    } finally { setSaving(false); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this project?")) return;
    try { await adminApi.deleteProject(id); toast.success("Project deleted"); load(); }
    catch { toast.error("Delete failed"); }
  };

  const importAbuDhabi = async () => {
    setImporting(true);
    try {
      const { data } = await adminApi.importAbuDhabi();
      toast.success(`Abu Dhabi: ${data.added} toegevoegd, ${data.updated} bijgewerkt`);
      load();
    } catch (err) {
      if (err.response?.status === 401) { clearToken(); nav("/admin"); }
      else toast.error("Import mislukt");
    } finally { setImporting(false); }
  };

  const openEdit = (p) => setForm({
    ...EMPTY, ...p,
    highlights: p.highlights?.length ? p.highlights : [""],
    payment: p.payment?.length ? p.payment : EMPTY.payment,
    gallery: p.gallery || [],
  });

  return (
    <div className="min-h-screen bg-cream" data-testid="admin-projects">
      <AdminHeader />
      <main className="mx-auto max-w-6xl px-5 py-10">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="font-display text-3xl font-semibold text-navy">Off-plan Projects</h1>
            <p className="mt-1 text-sm text-muted">Add and manage the projects shown on your website.</p>
          </div>
          <div className="flex gap-2">
            <button onClick={importAbuDhabi} disabled={importing} data-testid="admin-import-abudhabi" className="btn-ghost disabled:opacity-60">
              {importing ? <Loader2 size={16} className="animate-spin" /> : <Download size={16} />} Abu Dhabi projecten
            </button>
            <button onClick={() => setForm({ ...EMPTY })} data-testid="admin-new-project" className="btn-primary"><Plus size={16} /> New project</button>
          </div>
        </div>

        {loading ? (
          <div className="mt-16 flex justify-center"><Loader2 className="animate-spin text-gold" size={30} /></div>
        ) : projects.length === 0 ? (
          <div className="mt-10 rounded-3xl border border-dashed border-navy/20 bg-white p-12 text-center" data-testid="admin-projects-empty">
            <p className="font-display text-xl font-semibold text-navy">No projects yet</p>
            <p className="mt-2 text-sm text-muted">Until you add one, the site shows sample projects. Click “New project” to publish your own.</p>
          </div>
        ) : (
          <div className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {projects.map((p) => (
              <div key={p.id} className="overflow-hidden rounded-3xl bg-white shadow-soft" data-testid={`admin-project-${p.id}`}>
                <div className="relative aspect-[16/10] bg-sand">
                  {p.image ? <img src={p.image} alt={p.name} className="h-full w-full object-cover" /> : <div className="flex h-full items-center justify-center text-muted"><Building2 size={28} /></div>}
                  {!p.published && <span className="absolute left-3 top-3 rounded-full bg-navy/80 px-2.5 py-1 text-xs font-medium text-white">Draft</span>}
                  {p.tag && <span className="absolute right-3 top-3 rounded-full bg-white/90 px-2.5 py-1 text-xs font-medium text-navy">{p.tag}</span>}
                </div>
                <div className="p-5">
                  <p className="text-xs text-muted">{p.emirate || "Dubai"}{p.location ? ` · ${p.location}` : ""}{p.developer && ` · ${p.developer}`}</p>
                  <h3 className="mt-1 font-display text-lg font-semibold text-navy">{p.name}</h3>
                  <p className="mt-1 text-sm font-semibold text-gold">{p.price} {p.currency}</p>
                  <div className="mt-4 flex gap-2">
                    <button onClick={() => openEdit(p)} data-testid={`admin-project-edit-${p.id}`} className="flex flex-1 items-center justify-center gap-1.5 rounded-full border border-navy/15 py-2 text-xs font-medium text-navy transition hover:bg-navy hover:text-white"><Pencil size={13} /> Edit</button>
                    <button onClick={() => remove(p.id)} data-testid={`admin-project-delete-${p.id}`} className="flex items-center justify-center rounded-full border border-red-200 px-3 py-2 text-red-500 transition hover:bg-red-500 hover:text-white"><Trash2 size={14} /></button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </main>

      {form && (
        <div className="fixed inset-0 z-50 flex justify-end bg-navy/40 backdrop-blur-sm" onClick={() => !saving && setForm(null)}>
          <form onClick={(e) => e.stopPropagation()} onSubmit={save} data-testid="admin-project-form" className="h-full w-full max-w-xl overflow-y-auto bg-cream p-6 sm:p-8 shadow-2xl">
            <div className="flex items-center justify-between">
              <h2 className="font-display text-2xl font-semibold text-navy">{form.id ? "Edit project" : "New project"}</h2>
              <button type="button" onClick={() => setForm(null)} className="rounded-full p-2 text-navy hover:bg-navy/5"><X size={22} /></button>
            </div>

            <label className="mt-6 block text-xs font-medium text-navy/70">Cover photo</label>
            <div className="mt-2 flex items-center gap-4">
              <div className="h-24 w-32 shrink-0 overflow-hidden rounded-xl bg-sand">
                {form.image ? <img src={form.image} alt="" className="h-full w-full object-cover" /> : <div className="flex h-full items-center justify-center text-muted"><ImagePlus size={22} /></div>}
              </div>
              <label className="btn-ghost cursor-pointer"><ImagePlus size={15} /> Upload<input type="file" accept="image/*" className="hidden" data-testid="admin-project-image" onChange={onMainImage} /></label>
            </div>

            <label className="mt-5 block text-xs font-medium text-navy/70">Gallery (up to 6)</label>
            <div className="mt-2 flex flex-wrap gap-2">
              {form.gallery.map((g, i) => (
                <div key={i} className="relative h-16 w-20 overflow-hidden rounded-lg">
                  <img src={g} alt="" className="h-full w-full object-cover" />
                  <button type="button" onClick={() => removeGallery(i)} className="absolute right-0.5 top-0.5 rounded-full bg-navy/80 p-0.5 text-white"><X size={12} /></button>
                </div>
              ))}
              <label className="flex h-16 w-20 cursor-pointer items-center justify-center rounded-lg border border-dashed border-navy/25 text-muted hover:border-gold"><Plus size={18} /><input type="file" accept="image/*" multiple className="hidden" data-testid="admin-project-gallery" onChange={onGallery} /></label>
            </div>

            <label className="mt-5 block text-xs font-medium text-navy/70">Project name *</label>
            <input className={field} data-testid="admin-project-name" value={form.name} onChange={set("name")} placeholder="Mercer House" />

            <div className="mt-4 grid grid-cols-2 gap-3">
              <div><label className="block text-xs font-medium text-navy/70">Emirate</label>
                <select className={field} data-testid="admin-project-emirate" value={form.emirate} onChange={set("emirate")}>
                  <option value="Dubai">Dubai</option>
                  <option value="Abu Dhabi">Abu Dhabi</option>
                </select></div>
              <div><label className="block text-xs font-medium text-navy/70">Developer</label><input className={field} value={form.developer} onChange={set("developer")} placeholder="Ellington" /></div>
              <div><label className="block text-xs font-medium text-navy/70">Location</label><input className={field} value={form.location} onChange={set("location")} placeholder="Uptown Dubai" /></div>
              <div><label className="block text-xs font-medium text-navy/70">Bedrooms</label><input className={field} value={form.bedrooms} onChange={set("bedrooms")} placeholder="Studio – 4 BR" /></div>
              <div><label className="block text-xs font-medium text-navy/70">Size</label><input className={field} value={form.size} onChange={set("size")} placeholder="460 – 3,900 sqft" /></div>
              <div><label className="block text-xs font-medium text-navy/70">Handover</label><input className={field} value={form.handover} onChange={set("handover")} placeholder="Q3 2027" /></div>
              <div><label className="block text-xs font-medium text-navy/70">Tag</label><input className={field} value={form.tag} onChange={set("tag")} placeholder="New Launch" /></div>
              <div><label className="block text-xs font-medium text-navy/70">Price from</label><input className={field} value={form.price} onChange={set("price")} placeholder="1,150,828" /></div>
              <div><label className="block text-xs font-medium text-navy/70">Currency</label><input className={field} value={form.currency} onChange={set("currency")} /></div>
            </div>

            <label className="mt-4 block text-xs font-medium text-navy/70">Description</label>
            <textarea rows={3} className={`${field} resize-none`} data-testid="admin-project-description" value={form.description} onChange={set("description")} placeholder="About this development..." />

            <label className="mt-4 block text-xs font-medium text-navy/70">Highlights</label>
            {form.highlights.map((h, i) => (
              <div key={i} className="mt-2 flex gap-2">
                <input className={field} value={h} onChange={(e) => setHighlight(i, e.target.value)} placeholder="e.g. Rooftop infinity pool" />
                <button type="button" onClick={() => removeHighlight(i)} className="rounded-xl border border-navy/15 px-3 text-navy hover:bg-navy hover:text-white"><X size={14} /></button>
              </div>
            ))}
            <button type="button" onClick={addHighlight} className="mt-2 text-sm font-medium text-gold">+ Add highlight</button>

            <label className="mt-4 block text-xs font-medium text-navy/70">Payment plan</label>
            {form.payment.map((row, i) => (
              <div key={i} className="mt-2 grid grid-cols-2 gap-2">
                <input className={field} value={row.label} onChange={(e) => setPay(i, "label", e.target.value)} placeholder="On booking" />
                <input className={field} value={row.value} onChange={(e) => setPay(i, "value", e.target.value)} placeholder="20%" />
              </div>
            ))}

            <label className="mt-4 flex items-center gap-2 text-sm text-navy">
              <input type="checkbox" checked={form.published} onChange={set("published")} data-testid="admin-project-published" className="h-4 w-4 accent-gold" />
              Published (visible on website)
            </label>

            <button type="submit" disabled={saving} data-testid="admin-project-save" className="btn-primary mt-6 w-full disabled:opacity-60">
              {saving ? <><Loader2 size={16} className="animate-spin" /> Saving...</> : form.id ? "Update project" : "Publish project"}
            </button>
          </form>
        </div>
      )}
      <Toaster position="top-center" richColors />
    </div>
  );
}
